const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/utils/mathUtils.ts';
import _ from 'lodash';

const polygonTools: any = require('polygon-tools');

/**
 * A 2-dimensional point
 */
export interface IPoint2 {
    x: number;
    y: number;
}

/**
 * Limits the given value to a minimum and a maximum.
 * @param value value to be clamped
 * @param min lower boundary
 * @param max upper boundary
 * @returns The value clamped to the lower and upper boundary.
 */
export function clamp(value: number, min: number = 0, max: number = 1) {
    return Math.max(min, Math.min(max, value));
}

/**
 * Interpolates linearly between a minimum and a maximum when normalizedValue is
 * between 0..1. When normalizedValue is outside the 0..1 range, the function will
 * extrapolate linearly.
 * @param normalizedValue 0 will result in min, 1 will result in max
 * @param min lower boundary
 * @param max upper boundary
 * @returns A value between min..max when normalizedValue is between 0..1.
 */
export function interpolateLinearly(normalizedValue: number, min: number, max: number) {
    return min + normalizedValue * (max - min);
}

// noinspection JSUnusedGlobalSymbols
/**
 * Converts an angle from Degrees to Radians
 * @return Radians
 */
export function deg2rad(deg: number): number {
    return (deg * Math.PI) / 180.0;
}

/**
 * Rotates a given point around a given centre.
 * @param pt (in): The point to be rotated
 * @param phiRadians (in): The angle in Radians
 * @param origin (in): Rotation centre
 * @return The rotated point
 */
export function rotate(pt: IPoint2, phiRadians: number, origin: IPoint2): IPoint2 {
    const c = Math.cos(phiRadians);
    const s = Math.sin(phiRadians);
    const mx = pt.x - origin.x;
    const my = pt.y - origin.y;
    return {
        x: mx * c - my * s + origin.x,
        y: mx * s + my * c + origin.y,
    };
}

/**
 * Converts a decimal number to Roman
 * @return A string
 */
export function toRoman(num: number): string | null {
    if (typeof num !== 'number' || Number.isNaN(num) || num === Number.POSITIVE_INFINITY || num <= 0) {
        return null;
    }

    const lookup = [
        { c: 'M', n: 1000 },
        { c: 'CM', n: 900 },
        { c: 'D', n: 500 },
        { c: 'CD', n: 400 },
        { c: 'C', n: 100 },
        { c: 'XC', n: 90 },
        { c: 'L', n: 50 },
        { c: 'XL', n: 40 },
        { c: 'X', n: 10 },
        { c: 'IX', n: 9 },
        { c: 'V', n: 5 },
        { c: 'IV', n: 4 },
        { c: 'I', n: 1 },
    ];

    return lookup.reduce((result: string, cn) => {
        while (num >= cn.n) {
            result += cn.c;
            num -= cn.n;
        }
        return result;
    }, '');
}

// Polygon calculations

export interface PolygonPoint extends IPoint2 {
    neighbors?: number[];
}

export interface PolygonLine {
    fromIdx: number;
    toIdx: number;
    idx: number;
}

export interface Line {
    from: IPoint2;
    to: IPoint2;
    idx: number;
}

export interface Polygon {
    points: PolygonPoint[];
}

export interface PolygonResults {
    area: number;
    outline: number;
    numberPoints: number;
    segments: number;
    extentX: number;
    extentY: number;
}

export interface PolygonIntersection {
    line1: PolygonLine;
    line2: PolygonLine;
    at: PolygonPoint;
}

/**
 * Checks if two lines intersect. Adjacent lines may intersect at their joint.
 * @param line1 1st line
 * @param line2 2nd line
 * @param numLines number of total lines in polygon
 * @return true if lines intersect
 */
export function checkIntersectedLines(line1: Line, line2: Line, numLines: number): boolean {
    if (line1.idx === line2.idx) {
        return false;
    }
    const orientation = (p: IPoint2, q: IPoint2, r: IPoint2) => {
        const orientationValue = (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);
        if (orientationValue === 0) {
            return 0;
        }
        return orientationValue > 0 ? 1 : -1;
    };
    const checkOnSegment = (p: IPoint2, q: IPoint2, r: IPoint2) => {
        return (
            q.x <= Math.max(p.x, r.x) &&
            q.x >= Math.min(p.x, r.x) &&
            q.y <= Math.max(p.y, r.y) &&
            q.y >= Math.min(p.y, r.y)
        );
    };
    const o1 = orientation(line1.from, line1.to, line2.from);
    const o2 = orientation(line1.from, line1.to, line2.to);
    const o3 = orientation(line2.from, line2.to, line1.from);
    const o4 = orientation(line2.from, line2.to, line1.to);
    const intersect = o1 !== o2 && o3 !== o4;
    const neighboring =
        Math.abs(line1.idx - line2.idx) === 1 ||
        (line2.idx === 0 && line1.idx === numLines - 1) ||
        (line1.idx === 0 && line2.idx === numLines - 1);
    if (intersect) {
        return !neighboring;
    }
    if (o1 === 0 && checkOnSegment(line1.from, line2.from, line1.to) && !neighboring) {
        return true;
    }
    if (o2 === 0 && checkOnSegment(line1.from, line2.to, line1.to)) {
        return true;
    }
    if (o3 === 0 && checkOnSegment(line2.from, line1.from, line2.to)) {
        return true;
    }
    if (o4 === 0 && checkOnSegment(line2.from, line1.to, line2.to) && !neighboring) {
        return true;
    }
    return false;
}

/**
 * Convenience function to Check if two lines of a polygon intersect. Adjacent lines may intersect at their joint.
 * @param polygon
 * @param line1 1st line
 * @param line2 2nd line
 * @return true if lines intersect
 */
export function checkIntersectedLinesInPolygon(polygon: Polygon, line1: PolygonLine, line2: PolygonLine): boolean {
    return checkIntersectedLines(
        { from: polygon.points[line1.fromIdx], to: polygon.points[line1.toIdx], idx: line1.idx },
        { from: polygon.points[line2.fromIdx], to: polygon.points[line2.toIdx], idx: line2.idx },
        polygon.points.length
    );
}

/**
 * Calculate the intersection of two polygon lines
 * @param polygon
 * @param line1 1st line
 * @param line2 2nd line
 * @return intersection point
 */
export function calculateIntersection(polygon: Polygon, line1: PolygonLine, line2: PolygonLine): PolygonPoint | null {
    const line1To = polygon.points[line1.toIdx];
    const line1From = polygon.points[line1.fromIdx];
    const line2To = polygon.points[line2.toIdx];
    const line2From = polygon.points[line2.fromIdx];
    const neighbors = [line1.toIdx, line1.fromIdx, line2.toIdx, line2.fromIdx];
    // line1 is perpendicular
    if (line1To.x === line1From.x) {
        // if line2 is perpendicular also there is no intersection
        if (line2To.x === line2From.x) {
            return null;
        }
        const pitchLine2 = (line2To.y - line2From.y) / (line2To.x - line2From.x);
        const cutX = line1To.x;
        const cutY = line2From.y + pitchLine2 * (cutX - line2From.x);
        return { x: cutX, y: cutY, neighbors };
    }
    // line2 is perpendicular
    if (line2To.x === line2From.x) {
        const pitchLine1 = (line1To.y - line1From.y) / (line1To.x - line1From.x);
        const cutX = line2To.x;
        const cutY = line1From.y + pitchLine1 * (cutX - line1From.x);
        return { x: cutX, y: cutY, neighbors };
    }
    // no line is perpendicular, calculate pitches of both lines
    const pitchLine1 = (line1To.y - line1From.y) / (line1To.x - line1From.x);
    const pitchLine2 = (line2To.y - line2From.y) / (line2To.x - line2From.x);
    // if pitches are equal there is no intersection
    if (pitchLine1 === pitchLine2) {
        return null;
    }
    // use easy linear equation to calculate x of intersection
    const cutX =
        (pitchLine1 * line1From.x - line1From.y - pitchLine2 * line2From.x + line2From.y) / (pitchLine1 - pitchLine2);
    // use x to calculate y
    const cutY = line1From.y + pitchLine1 * (cutX - line1From.x);
    return { x: cutX, y: cutY, neighbors };
}

/**
 * Checks if a polygon has intersecting lines and calculated the intersection points
 * @param polygon polygon to test
 * @return array of intersections; this is empty if polygon is "pure"
 */
export function determineIntersectionsPolygon(polygon: Polygon): PolygonIntersection[] {
    const numPoints = polygon.points.length;
    const intersections: PolygonIntersection[] = [];
    for (let a = 0; a < numPoints; a++) {
        for (let b = a + 1; b < numPoints; b++) {
            const line1 = { fromIdx: a, toIdx: (a + 1) % numPoints, idx: a };
            const line2 = { fromIdx: b, toIdx: (b + 1) % numPoints, idx: b };
            if (checkIntersectedLinesInPolygon(polygon, line1, line2)) {
                const intersection = calculateIntersection(polygon, line1, line2);
                if (intersection) {
                    // check for intersections at the same point; if there is any, just update that (neighbors) and discard the new one
                    const existingIntersection: PolygonIntersection | undefined = _.find(
                        intersections,
                        (testIntersection: PolygonIntersection) => {
                            return testIntersection.at.x === intersection.x && testIntersection.at.y === intersection.y;
                        }
                    );
                    if (existingIntersection) {
                        existingIntersection.at.neighbors = _.uniq(
                            _.flatten([existingIntersection.at.neighbors, intersection.neighbors])
                        ) as number[];
                    } else {
                        intersections.push({ line1, line2, at: intersection });
                    }
                }
            }
        }
    }
    return intersections;
}

/**
 * Convenience function to check if a polygon has any intersections
 * @param polygon polygon to test
 * @return true if polygon has intersections
 */
export function checkIntersectedPolygon(polygon: Polygon): boolean {
    return determineIntersectionsPolygon(polygon).length > 0;
}

export function checkPointOnLineInPolygon(polygon: Polygon, line: PolygonLine, point: PolygonPoint): boolean {
    const lineTo = polygon.points[line.toIdx];
    const lineFrom = polygon.points[line.fromIdx];
    // line is perpendicular
    if (lineTo.x === lineFrom.x) {
        if (point.x !== lineTo.x) {
            return false;
        }
        return _.inRange(point.y, lineTo.y, lineFrom.y) || point.y === lineTo.y || point.y === lineFrom.y;
    }
    if (!_.inRange(point.x, lineTo.x, lineFrom.x) && point.x !== lineTo.x && point.x !== lineFrom.x) {
        return false;
    }
    const expectedY = lineFrom.y + ((lineTo.y - lineFrom.y) / (lineTo.x - lineFrom.x)) * (point.x - lineFrom.x);
    return expectedY === point.y;
}
/**
 * Cut polygon into one or more (non intersecting) sub polygons if there are intersections
 * @param polygonOriginal polygon to cut
 * @return an array of one or more polygons without intersections
 */
export function cutPolygonAtLineCrossings(polygonOriginal: Polygon): Polygon[] {
    // clean up polygon first, also creates a deep clone
    const polygon = cleanPolygon(polygonOriginal);
    // calculate intersections
    const intersections: PolygonIntersection[] = determineIntersectionsPolygon(polygon);
    // return polygon if there are no intersections
    if (
        intersections.length === 0 &&
        !_.find(polygon.points, (point: PolygonPoint) => {
            return point.neighbors && point.neighbors.length > 2;
        })
    ) {
        return [polygon];
    }
    const polygonSegments: Polygon[] = [];
    // insert intersection points in point graph
    _.forEach(intersections, (intersection: PolygonIntersection) => {
        const existingPointOnIntersection: PolygonPoint | undefined = _.find(polygon.points, (point: PolygonPoint) => {
            return point.x === intersection.at.x && point.y === intersection.at.y;
        });
        if (existingPointOnIntersection) {
            existingPointOnIntersection.neighbors = _.uniq(
                _.flatten([existingPointOnIntersection.neighbors, intersection.at.neighbors])
            ) as number[];
        } else {
            polygon.points.push(intersection.at);
        }
    });
    // Adjust neighbors
    let hadAdjustments = true;
    while (hadAdjustments) {
        hadAdjustments = false;
        _.forEach(polygon.points, (point: PolygonPoint, pidx: number) => {
            _.forEach(point.neighbors, (neighbor: number, nidx: number) => {
                _.forEach(polygon.points, (checkPoint: PolygonPoint, cpidx: number) => {
                    if (pidx === cpidx || neighbor === cpidx) {
                        return;
                    }
                    if (
                        point.neighbors &&
                        checkPointOnLineInPolygon(polygon, { fromIdx: pidx, toIdx: neighbor, idx: 0 }, checkPoint)
                    ) {
                        point.neighbors[nidx] = cpidx;
                        //hadAdjustments = true;
                    }
                });
            });
        });
    }
    _.forEach(polygon.points, (point: PolygonPoint, pidx: number) => {
        point.neighbors = _.filter(point.neighbors, (neighbor: number) => {
            return neighbor !== pidx;
        });
    });
    // walk through the graph and try to cut out sub polygons until there are no intersections left
    let hasIntersections = true;
    while (hasIntersections) {
        hasIntersections = false;
        _.forEach(polygon.points, (point: PolygonPoint, pointIdx: number) => {
            // intersection points have four neighbors, only check on these
            if (!point.neighbors || point.neighbors.length < 3) {
                return;
            }
            // go through the graph starting with each neighbor of the intersection
            _.forEach(point.neighbors, (startNeighbor: number) => {
                // create a new polygon segment first
                let polygonSegment: Polygon | null = {
                    points: [polygon.points[pointIdx]],
                };
                let endNeighbor = startNeighbor;
                let lastNeighbor = pointIdx;
                // walk through the graph and add all encountered points to the polygon segment until we:
                // - come back to the start (intersection point) -> the new polygon segment is valid
                // - encounter a point that has already been cut from the graph -> the polygon is invalid and discarded
                // - encounter another intersection -> the polygon is invalid and discarded
                while (true) {
                    if (endNeighbor === pointIdx) {
                        break;
                    }
                    if (polygon.points[endNeighbor].neighbors?.length !== 2) {
                        polygonSegment = null;
                        break;
                    }
                    polygonSegment.points.push(polygon.points[endNeighbor]);
                    [endNeighbor, lastNeighbor] = [
                        polygon.points[endNeighbor].neighbors?.[0] === lastNeighbor
                            ? (polygon.points[endNeighbor].neighbors?.[1] as number)
                            : (polygon.points[endNeighbor].neighbors?.[0] as number),
                        endNeighbor,
                    ];
                }
                // If we found a valid polygon segment we cut it from the graph:
                // - Normal polygon points are marked as cut by removing their neighbors value
                // - Intersection points become normal polygon points by removing two of their four neighbors
                if (polygonSegment) {
                    hasIntersections = true;
                    polygonSegments.push(polygonSegment);
                    _.forEach(polygonSegment.points, (point: PolygonPoint) => {
                        if (point.neighbors?.length === 2) {
                            point.neighbors = undefined;
                        }
                        if (point.neighbors?.length === 4) {
                            point.neighbors = _.filter(point.neighbors, (neighborsNeighbor: number) => {
                                return neighborsNeighbor !== startNeighbor && neighborsNeighbor !== lastNeighbor;
                            });
                        }
                    });
                }
            });
        });
    }
    // return array of polygons
    return polygonSegments;
}

/**
 * Remove duplicate consecutive points in a polygon. Also removes end point if identical to start point.
 * @param polygon Polygon to clean
 * @return polygon clone with duplicate points removed
 */
export function cleanPolygon(polygon: Polygon): Polygon {
    const cleansedPolygon: Polygon = { points: [] };
    let lastPoint: IPoint2 | null = null;
    // if two or more adjacent points are identical, delete all but the first
    for (const point of polygon.points) {
        if (lastPoint && lastPoint.x === point.x && lastPoint.y === point.y) {
            continue;
        }
        lastPoint = point;
        cleansedPolygon.points.push({ x: point.x, y: point.y, neighbors: [] });
    }
    // if start and end point are identical delete the end point
    if (lastPoint && lastPoint.x === cleansedPolygon.points[0].x && lastPoint.y === cleansedPolygon.points[0].y) {
        cleansedPolygon.points.pop();
    }
    // setup neighbors
    const cleansedPolygonSize = cleansedPolygon.points.length;
    for (let i = 0; i < cleansedPolygonSize; i++) {
        cleansedPolygon.points[i].neighbors?.push?.((i + 1) % cleansedPolygonSize);
        cleansedPolygon.points[i].neighbors?.push?.(i === 0 ? cleansedPolygonSize - 1 : i - 1);
    }
    // identical points are bad for all further calculations; if we encounter this, merge them as intersection
    _.forEach(cleansedPolygon.points, (point: PolygonPoint, pidx: number) => {
        _.forEach(cleansedPolygon.points, (testPoint: PolygonPoint, tpidx: number) => {
            if (pidx === tpidx) {
                return;
            }
            if (!point.neighbors || !testPoint.neighbors) {
                return;
            }
            if (testPoint.x === point.x && testPoint.y === point.y) {
                point.neighbors = _.uniq(_.flatten([point.neighbors, testPoint.neighbors])) as number[];
                _.forEach(testPoint.neighbors, (neighbor: number) => {
                    const pointNeighbors = cleansedPolygon.points[neighbor].neighbors;
                    const replaceNeighborIndex = _.indexOf(pointNeighbors, tpidx);
                    if (replaceNeighborIndex >= 0 && pointNeighbors) {
                        pointNeighbors[replaceNeighborIndex] = pidx;
                    }
                });
                testPoint.neighbors = undefined;
            }
        });
    });
    cleansedPolygon.points = _.filter(cleansedPolygon.points, (point: PolygonPoint, pidx: number) => {
        if (!point.neighbors) {
            _.forEach(cleansedPolygon.points, (adjustPoint: PolygonPoint) => {
                if (adjustPoint.neighbors) {
                    adjustPoint.neighbors = _.map(adjustPoint.neighbors, (neighbor: number) => {
                        return neighbor < pidx ? neighbor : neighbor - 1;
                    });
                }
            });
            return false;
        }
        return true;
    });
    // return cleansed polygon
    return cleansedPolygon;
}

/**
 * Calculate area and outline length of a given Polygon; divide into polygon segments first if there are intersections
 * @param polygonOriginal Polygon object
 * @return results object with area (if polygon has no intersecting lines), outline, number of points and intersection warning
 */
export function calculatePolygon(polygonOriginal: Polygon): PolygonResults {
    const polygon = cleanPolygon(polygonOriginal);
    const polygonSegments = cutPolygonAtLineCrossings(polygonOriginal);
    const numberPoints = polygon.points.length;
    let outline = 0;
    _.forEach(polygonSegments, (segment: Polygon) => {
        let suboutline = 0;
        const subnumberpoints = segment.points.length;
        for (let i = 0; i < subnumberpoints; i++) {
            const ptA = segment.points[i];
            const ptB = segment.points[(i + 1) % subnumberpoints];
            suboutline += Math.sqrt((ptB.x - ptA.x) ** 2 + (ptB.y - ptA.y) ** 2);
        }
        outline += suboutline;
    });
    let area: number = 0;
    const polygonPoints = _.map(polygonOriginal.points, (point: PolygonPoint) => [point.x, point.y]);
    if (polygonPoints.length > 2) {
        try {
            // When the raw polygon size is 0 (only happens with precise test models with an intersecting outline, so virtually impossible
            // on hand drawn polygons) the polygon is discarded by the tesselator and the calculation fails. So we nudge the first point a
            // little bit to get a positive size.
            for (let nudge = 0; nudge < 10 && polygonTools.polygon.area(polygonPoints) == 0; nudge++) {
                polygonPoints[0][0] += 0.000001;
            }
            const triangles: any = polygonTools.tesselator.run({
                polygons: [polygonPoints],
                vertexSize: 2,
                windingRule: polygonTools.tesselator.GLU_TESS_WINDING_NONZERO,
                boundaryOnly: false,
                normal: null,
                autoWinding: true,
            });
            area = _.reduce(
                triangles[0],
                (area: number, triangle: any) => {
                    const triangleArea = polygonTools.polygon.area(triangle);
                    return area + Math.abs(triangleArea);
                },
                0
            );
        } catch (e) {
            console.log('Could not calculate area of polygon; reverting to simplest method available', e);
            area = polygonTools.polygon.area(polygonPoints); // this may well be 0
        }
    }
    const xCoordinates = polygonOriginal.points.map((point: IPoint2) => point.x);
    const yCoordinates = polygonOriginal.points.map((point: IPoint2) => point.y);
    const extentX = Math.max(...xCoordinates) - Math.min(...xCoordinates);
    const extentY = Math.max(...yCoordinates) - Math.min(...yCoordinates);
    return {
        area,
        outline,
        numberPoints,
        segments: 1,
        extentX,
        extentY,
    };
}
